import type { Point } from "@clintonelec/react-storybook";
import type { IndexSignature } from "@clintonelec/typescriptutils";
import type {
	ShutterSpeed, BitrateControl, Codec, ExposureMode, ImageQuality, ManualWbMode, MaxAgc, MirrorImage, Profile,
	UseStreamSettings, Colors, DeviceNamePosition, WhiteBalanceMode, SlowShutter,
	AntiFlickerMode, PIrisControl, ShutterSpeed50Hz, BacklightCompensation, HighlightCompensation,
	DigitalNoiseReduction, DefogControl, EnhancementActivation, DayNightMode, DwellTime, IlluminationMode, IrLed,
	WhiteLedControl, AiRuleType, AiRuleDirection
} from "Data/Objects/Camera";

export type Stream = {
	codec?: Codec;
	profile?: Profile;
	resolution?: string;
	frameRate: number;
	gopSize?: number;
	bitrateControl?: BitrateControl;
	bitrate?: number;
	imageQuality?: ImageQuality;
	mjpegQuality?: number;
	useStreamSettings?: UseStreamSettings;
	frameRateLabel?: number;
};

export type FourthStream = Stream & {
	frameRateLabel: number;
};

export type CameraInfo = {
	resolution: boolean;
	dateTime: boolean;
	deviceName: DeviceNamePosition;
};

export type Ai = {
	displayMode: boolean;
	objectColor: Colors;
	ruleColor: Colors;
	eventColor: Colors;
};

export type AppearanceSettings = {
	mirrorImage: MirrorImage;
	brightness: number;
	contrast: number;
	color: number;
	sharpness: number;
	whiteBalanceMode: WhiteBalanceMode;
	manualWbMode: ManualWbMode;
};

export type ExposureSettings = {
	exposureMode: ExposureMode;
	agcGain: number;
	eShutterSpeed: number;
	maxAgc: MaxAgc;
	baseShutterSpeed: ShutterSpeed | ShutterSpeed50Hz;
	maxShutterSpeed: ShutterSpeed | ShutterSpeed50Hz;
	slowShutter: SlowShutter;
	antiFlickerMode: AntiFlickerMode;
	pIrisControl: PIrisControl;
};

export type EnhancementSettings = {
	wideDynamicRange: boolean;
	backlightCompensation: BacklightCompensation;
	highlightCompensation: HighlightCompensation;
	digitalNoiseReduction: DigitalNoiseReduction;
	defogControl: DefogControl;
	enhancementActivation: EnhancementActivation;
};

export type DayNightSettings = {
	dayNightMode: DayNightMode;
	dwellTime: DwellTime;
	dayTimeStart: number;
	dayTimeEnd: number;
	dayToNightSensitivity: number;
	nightToDaySensitivity: number;
	illuminationMode: IlluminationMode;
	irLed: IrLed;
	adaptiveIr: boolean;
	irLedIllumination: number;
	antiOverexposure: boolean;
	whiteLedControl: WhiteLedControl;
	whiteLedIllumination: number;
};

export type ZoomFocusSettings = {
	zoomFocusArea: [ Point, Point, Point, Point ];
	zoom: number;
	focus: number;
	iris: string;
	dayNightFocusShift: boolean;
};

export type Counter = {
	enabled: boolean;
	notificationEnabled: boolean;
	notificationCounter: number;
	resetCounterAfterNotification: boolean;
	increaseRule: number;
	decreaseRule: number;
	point: Point;
	count: number;
};

export type BaseAiRule = {
	type: AiRuleType;
	objectTypes: {
		human: boolean;
		vehicle: boolean;
		bike: boolean;
	};
	confidenceThreshold: number;
	audioFile: string;
	schedule: IndexSignature<boolean>;
	points: Point[];
	counter: Counter;
};

export type LineRule = {
	type: AiRuleType.LINE;
	direction: AiRuleDirection;
} & BaseAiRule;

export type ZoneRule = {
	type: AiRuleType.ZONE;
	intrusion: boolean;
	enter: boolean;
	exit: boolean;
	loiter: boolean;
	loiterDuration: number;
} & BaseAiRule;

export type AiRule = LineRule | ZoneRule | null;

export type DynamicZoom = {
	enabled: boolean;
	zoomLevel: number;
	duration: string;
	threshold: string;
	analytics: boolean;
	motion: boolean;
	alarmIn: boolean;
};

export type ImageFile = {
	name: string;
	dataUrl: string;
};

export type BannerMode = "Single" | "Dual" | "Sequence";
export type BorderColor = "Red" | "Orange" | "Yellow" | "Green" | "Cyan" | "Blue" | "Magenta" | "Black" | "White";
export type BannerLocation = "Top" | "Bottom";
export type BannerDisplayMode = "Event" | "Always";
export type LogoAlarmDisplayMode = "Show Camera on Alarm" | "Show Logo on Alarm";
export type BannerEventTypes = "Motion" | "Analytics" | "Alarm In";

export const LOGO_EVENT_TYPES = [
	"Motion",
	"Analytics"
] as const satisfies ReadonlyArray<string>;

export const LOGO_DISPLAY_MODE_TYPES = [
	"camera",
	"logo"
] as const satisfies ReadonlyArray<string>;

export type LogoDisplayModeType = (typeof LOGO_DISPLAY_MODE_TYPES)[number];
export type LogoEventType = (typeof LOGO_EVENT_TYPES)[number];
